import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import CustomCursor from './animations/CustomCursor';
import Loader from './components/Loader';
import Navbar from './components/layout/Navbar';
import ModernFooter from './components/layout/ModernFooter';
import ScrollToTop from './components/ScrollToTop';
import ScrollButton from './components/ScrollToTop/ScrollButton';
import './styles/globals.css';
import './styles/parallax.css';import './styles/footer.css';
import './index.css';

// Import des pages
import Home from './pages/home/Home';
import Pedagogy from './pages/pedagogy/Pedagogy';
import Events from './pages/events/Events';
import EventDetail from './pages/events/EventDetail';
import Blog from './pages/blog/Blog';
import Registration from './pages/registration/Registration';
import About from './pages/about/About';
import Contact from './pages/contact/Contact';
import NotFound from './pages/NotFound';

// Import des pages admin
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { UserDashboard } from './pages/admin/UserDashboard';
import AdminLogin from './pages/admin/Login';
import UsersManagement from './pages/admin/UsersManagement';
import EventsManagement from './pages/admin/EventsManagement';
import StatsManagement from './pages/admin/StatsManagement';
import NewsManagement from './pages/admin/NewsManagement';

// Composant pour vérifier l'authentification (version améliorée)
const RequireAuth = () => {
  const { user, loading } = useAuth();
  const token = localStorage.getItem('token');
  
  // Pendant le chargement, afficher un indicateur de chargement
  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }
  
  // Si l'utilisateur n'est pas connecté, rediriger vers la page de login
  if (!user && !token) {
    console.log('Utilisateur non connecté, redirection vers la page de login');
    return <Navigate to="/admin/login" replace />;
  }
  
  return <Outlet />;
};

// Composant pour afficher conditionnellement la barre de navigation
const NavbarWrapper = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname.includes('/admin/') || location.pathname.includes('/user/');
  
  return (
    <>
      {!isAdminPage && <Navbar />}
      {children}
      {!isAdminPage && <ModernFooter />}
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <HelmetProvider>
        <AppProvider>
          <Loader />
        </AppProvider>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <AppProvider>
        <AuthProvider>
          <div className="app-container w-full overflow-x-hidden">
            <Router>
              <ScrollToTop />
              <NavbarWrapper>
                <Routes>
                  {/* Routes publiques */}
                  <Route path="/" element={<Home />} />
                  <Route path="/pedagogy" element={<Pedagogy />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/events/:id" element={<EventDetail />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/registration" element={<Registration />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  {/* Routes admin sans Navbar */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  
                  {/* Routes protégées sans Navbar */}
                  <Route element={<RequireAuth />}>
                    <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/admin/users" element={<ProtectedRoute><UsersManagement /></ProtectedRoute>} />
                    <Route path="/admin/events" element={<ProtectedRoute><EventsManagement /></ProtectedRoute>} />
                    <Route path="/admin/stats" element={<ProtectedRoute><StatsManagement /></ProtectedRoute>} />
                    <Route path="/admin/news" element={<ProtectedRoute><NewsManagement /></ProtectedRoute>} />
                  </Route>
                  
                  <Route path="/user" element={<RequireAuth />}>
                    <Route path="dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                  </Route>
                  
                  {/* Route 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </NavbarWrapper>
              <ScrollButton />
            </Router>
          </div>
        </AuthProvider>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;
